import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Card, CardHeader, CardContent, Button, Typography, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, CircularProgress
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { IconButton, Tooltip } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import MenuItem from "@mui/material/MenuItem";
import { capitalizar } from "../../utils/capitalizar";
import { expedienteRadicacionJWT, getCombosJWT, guardarRadicacionJWT, eliminarRadicacionHistorialJWT, expedienteAutosJWT, guardarAutoJWT, expedienteCartaPoderJWT } from "../../api/endpoints";
import { normalizeKeys } from "../../utils/normalizeKeys";
import usePermisosModulo from "../../hooks/usePermisosModulo";

const COLOR_HEADER = "#5d7b9d";
const COLOR_HEADER_TEXT = "#fff";
const COLOR_ROW_ALT = "#f9f9f9";

const RESULTADOS = [
  { value: "", label: "" },
  { value: "Favorable", label: "Favorable" },
  { value: "Desfavorable", label: "Desfavorable" },
  { value: "Parcialmente Favorable", label: "Parcialmente Favorable" }
];

// --- Helpers front ---
const safeCap = (v) => capitalizar(v || "—");
const n0 = (v) => Number(v ?? 0);
const normalizarMontoOutbound = (v) => {
  if (v == null) return "";
  const s = String(v).trim();
  // quita separadores de miles (.) y convierte coma decimal a punto
  return s.replace(/\./g, "").replace(",", ".");
};

// Formato AR con separador de miles para mostrar mientras el usuario tipea.
// Acepta tipeo libre y reformatea con puntos cada 3 dígitos. Coma para decimales.
const formatMilesAR = (raw) => {
  if (raw == null || raw === '') return '';
  const s = String(raw);
  // Limpiar todo lo que no sea dígito, coma o signo menos
  const cleaned = s.replace(/[^\d,-]/g, '');
  if (!cleaned) return '';
  const neg = cleaned.startsWith('-');
  const sinSigno = cleaned.replace(/-/g, '');
  const [intPart, decPart] = sinSigno.split(',');
  const soloDigitos = (intPart || '').replace(/\D/g, '');
  // Inserta separador de miles cada 3 dígitos desde la derecha
  const intFormatted = soloDigitos.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const result = (neg ? '-' : '') + (intFormatted || '0') + (decPart !== undefined ? ',' + decPart.slice(0, 2) : '');
  return result;
};

// Símbolo de moneda para display
const moneySymbol = (moneda) => moneda === 'USD' ? 'U$D ' : '$ ';

// Convertir fecha a formato ISO yyyy-MM-dd (para inputs type=date)
const toISO = (v) => {
    if (!v) return "";
    const s = String(v).trim();
    // /Date(1692921600000)/
    const m = s.match(/^\/Date\((\d+)\)\/$/);
    if (m) {
        const d = new Date(Number(m[1]));
        if (!isNaN(d.valueOf())) {
            const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
            const dd = String(d.getUTCDate()).padStart(2, "0");
            return `${d.getUTCFullYear()}-${mm}-${dd}`;
        }
    }
    // yyyy-mm-dd ya ok
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
    // dd/MM/yyyy
    const dmy = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
    // fallback Date.parse
    const d2 = new Date(s);
    if (!isNaN(d2.valueOf())) {
        const mm = String(d2.getMonth() + 1).padStart(2, "0");
        const dd = String(d2.getDate()).padStart(2, "0");
        return `${d2.getFullYear()}-${mm}-${dd}`;
    }
    return "";
};

// Normalizar MAYÚSCULAS → Título
const toTitleCase = (str) => {
    if (!str) return '';
    if (str !== str.toUpperCase()) return str;
    return str.toLowerCase().replace(/(?:^|\s|[-/(])\S/g, c => c.toUpperCase());
};

// Resaltar coincidencia de búsqueda
const highlightMatch = (text, query) => {
    if (!query || query.length < 2 || !text) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
        <>{text.slice(0, idx)}<strong style={{ color: '#1565c0' }}>{text.slice(idx, idx + query.length)}</strong>{text.slice(idx + query.length)}</>
    );
};

// Estilo zebra
const zebraStyle = (index) => ({
    backgroundColor: index % 2 === 1 ? '#f5f7fa' : 'transparent',
});

// Mapeo para datos viejos que puedan tener "Parcial" en vez de "Parcialmente Favorable"
const verResultado = (v) => {
  if (v === "Parcial") return "Parcialmente Favorable";
  return v || "—";
};

// Un monto editable en línea (moneda + importe). Lo usan los tres montos de los módulos que
// NO son Ejecuciones, que tienen exactamente el mismo comportamiento.
// La moneda es una sola por expediente (Expedientes.Moneda): cambiarla acá la cambia para todos.
function MontoEditable({ label, valor, moneda, canUpdate, style, onGuardar }) {
    const [editando, setEditando] = useState(false);
    const [monto, setMonto] = useState("");
    const [mon, setMon] = useState(moneda || 'ARS');

    const empezar = () => {
        setMonto(formatMilesAR(String(valor ?? '').replace('.', ',')));
        setMon(moneda || 'ARS');
        setEditando(true);
    };
    const guardar = async () => {
        // Si falla queda abierto para reintentar, sin perder lo tipeado
        if (await onGuardar(normalizarMontoOutbound(monto), mon || 'ARS')) setEditando(false);
    };

    return (
        <Box style={style}>
            <b>{label}:</b>
            {editando ? (
                <>
                    <TextField select value={mon} onChange={e => setMon(e.target.value)} size="small"
                        style={{ width: 80, marginLeft: 8 }} SelectProps={{ native: true }}>
                        <option value="ARS">ARS</option>
                        <option value="USD">USD</option>
                    </TextField>
                    <TextField value={monto} onChange={e => setMonto(formatMilesAR(e.target.value))}
                        type="text" size="small" style={{ width: 160, marginLeft: 8 }} placeholder="0,00" />
                    <Button size="small" onClick={guardar} color="primary" style={{ marginLeft: 8 }} variant="contained">
                        Guardar
                    </Button>
                    <Button size="small" onClick={() => setEditando(false)} style={{ marginLeft: 4 }}>
                        Cancelar
                    </Button>
                </>
            ) : (
                <>
                    &nbsp;{moneySymbol(moneda)}{n0(valor).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {canUpdate && (
                        <Button size="small" style={{ marginLeft: 8 }} onClick={empezar}>Editar</Button>
                    )}
                </>
            )}
        </Box>
    );
}

export default function RadicacionTab({ idExpediente, puedeEditar, idModulo, modulo }) {
  const { permDe } = usePermisosModulo(modulo);
  const perm = permDe('PESTAÑA RADICACION', 'RADICACION');
  // puedeEditar (genérico) + permiso específico de función
  const canUpdate = puedeEditar && perm.update;
  const canDelete = puedeEditar && perm.delete;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [historial, setHistorial] = useState([]);
  const [loadingHistorial] = useState(false);

  const [juzgados, setJuzgados] = useState([]);
  const [domicilios, setDomicilios] = useState([]);
  const [selectedDomicilio, setSelectedDomicilio] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);

  // Edición de montos + moneda (default ARS).
  // Hay dos campos editables:
  //   - editingMontoDemanda: el "Monto demanda" (manual, gana sobre certificados si está seteado)
  //   - editingMontoAct: el "Monto actualizado demanda" (manual, sobreescribe el calculado)
  // Cada uno tiene su propio estado pero comparten la moneda (un expediente = una moneda).
  const [editingMontoDemanda, setEditingMontoDemanda] = useState(false);
  const [nuevoMontoDemanda, setNuevoMontoDemanda] = useState("");
  const [editingMontoAct, setEditingMontoAct] = useState(false);
  const [nuevoMonto, setNuevoMonto] = useState("");
  const [nuevaMoneda, setNuevaMoneda] = useState("ARS");

  // AUTOS
  const [autosDraft, setAutosDraft] = useState([]);
  const [editRow, setEditRow] = useState(null);

    // Confirmación de eliminación (historial)
    const [deleteDlg, setDeleteDlg] = useState({ open: false, target: null });

    const pedirConfirmDelete = (row) => {
        setDeleteDlg({ open: true, target: row });
    };

    const cerrarDeleteDlg = () => setDeleteDlg({ open: false, target: null });

    async function handleDeleteHistorial() {
        const target = deleteDlg.target;
        cerrarDeleteDlg();
        if (!target?.id) return;
        try {
            const resp = await eliminarRadicacionHistorialJWT(idExpediente, target.id);
            if (resp?.ok) {
                openSnack("Historial eliminado", "success");
                fetchAll();
            } else {
                openSnack(resp?.mensaje || "Error al eliminar", "error");
            }
        } catch (err) {
            console.error("Error eliminando historial:", err);
            openSnack("Error al eliminar historial", "error");
        }
    }

    const nombreJuzgadoPorId = useCallback((val) => {
        if (val === undefined || val === null || val === "") return "—";
        const txt = String(val);
        // Buscar por ID en la lista
            const j = juzgados.find(x => String(x.id ?? x.Id) === txt);
        if (j) return (j.nombre || j.Nombre || "—");
        // Si lo que vino ya es texto (no número), devolvémoslo
            if (/\D/.test(txt)) return txt;
        return "—";
        }, [juzgados]);

  useEffect(() => {
    fetchAll();
    fetchAutos();
    // eslint-disable-next-line
  }, [idExpediente]);

  async function fetchAll() {
    setLoading(true);
    try {
      const [radResp, juzResp] = await Promise.all([
        expedienteRadicacionJWT(idExpediente),
        getCombosJWT('juzgados'),
      ]);

      // Datos principales
      const rad = radResp?.radicacion ? normalizeKeys(radResp.radicacion) : null;
      if (rad) {
        setData({
          ...rad,
          id_juzgado: rad.id_juzgado || rad.id_Juzgado || null,
          juzgado: rad.juzgado || '',
          apoderado: rad.apoderado || '',
          domicilio: rad.domicilio || '',
          carta_poder: rad.carta_poder || '',
          monto_demanda: rad.monto_demanda || '',
          monto_actualizado: rad.monto_actualizado || '',
          moneda: rad.moneda || 'ARS',
          presupuesto_costas: rad.presupuesto_costas || '',
          ubicacion: rad.ubicacion || '',
        });
      } else {
        setData(null);
      }

      // Historial de radicación
      setHistorial(rad?.historial || []);

      // Juzgados from catalog
      const juzItems = normalizeKeys(juzResp?.items || []);
      setJuzgados(juzItems.map(j => ({ id: j.id, nombre: j.label || j.juzgado || '' })));

      // Domicilios - not yet available
      setDomicilios([]);
      setSelectedDomicilio(null);
    } catch (e) {
      console.error('Error al cargar radicación:', e);
    } finally {
      setLoading(false);
    }
  }

    async function fetchAutos() {
        try {
            const resp = await expedienteAutosJWT(idExpediente);
            if (resp?.ok && resp.autos) {
                setAutosDraft(resp.autos.map(a => ({
                    ...a,
                    fecha: a.fecha ? new Date(a.fecha).toLocaleDateString('es-AR') : '',
                    resultado: a.resultado === "Parcial" ? "Parcialmente Favorable" : (a.resultado || ""),
                })));
            } else {
                setAutosDraft([]);
            }
        } catch (e) {
            console.error('Error al cargar autos:', e);
            setAutosDraft([]);
        }
    }

    async function handleSaveMontoActualizado() {
        try {
            const resp = await guardarRadicacionJWT(idExpediente, {
                monto_actualizado: normalizarMontoOutbound(nuevoMonto),
                moneda: nuevaMoneda || 'ARS',
            });
            if (resp?.ok) {
                openSnack("Monto actualizado guardado", "success");
                fetchAll();
            } else {
                openSnack(resp?.mensaje || "Error al guardar monto", "error");
            }
        } catch (err) {
            console.error("Error guardando monto:", err);
            openSnack("Error al guardar monto", "error");
        }
        setEditingMontoAct(false);
    }

    async function handleSaveMontoDemanda() {
        try {
            const resp = await guardarRadicacionJWT(idExpediente, {
                monto_demanda: normalizarMontoOutbound(nuevoMontoDemanda),
                moneda: nuevaMoneda || 'ARS',
            });
            if (resp?.ok) {
                openSnack("Monto demanda guardado", "success");
                fetchAll();
            } else {
                openSnack(resp?.mensaje || "Error al guardar monto", "error");
            }
        } catch (err) {
            console.error("Error guardando monto:", err);
            openSnack("Error al guardar monto", "error");
        }
        setEditingMontoDemanda(false);
    }

    // Guarda uno de los montos de los módulos que NO son Ejecuciones. Devuelve true si
    // salió bien, para que MontoEditable cierre la edición.
    async function guardarMontoNoEjecucion(campo, valor, moneda, etiqueta) {
        try {
            const resp = await guardarRadicacionJWT(idExpediente, { [campo]: valor, moneda });
            if (resp?.ok) {
                openSnack(`${etiqueta} guardado`, "success");
                fetchAll();
                return true;
            }
            openSnack(resp?.mensaje || "Error al guardar monto", "error");
        } catch (err) {
            console.error("Error guardando monto:", err);
            openSnack("Error al guardar monto", "error");
        }
        return false;
    }

    async function handleSaveAuto(idx) {
        try {
            const row = autosDraft[idx];
            const resp = await guardarAutoJWT(idExpediente, {
                instancia: idx,
                autos: row?.autos || '',
                fecha: row?.fecha || null,
                resultado: row?.resultado || '',
            });
            if (resp?.ok) {
                openSnack("Auto guardado correctamente", "success");
                fetchAutos();
            } else {
                openSnack(resp?.mensaje || "Error al guardar auto", "error");
            }
        } catch (err) {
            console.error("Error al guardar auto:", err);
            openSnack("Error al guardar auto", "error");
        }
        setEditRow(null);
    }

    const textoJuzgado = useMemo(() => {
        if (!data) return "—";
        return data.juzgado || "—";
    }, [data]);

    const [cartaInfo, setCartaInfo] = useState(null);
    const [loadingCarta, setLoadingCarta] = useState(false);
    const [cartaOpen, setCartaOpen] = useState(false);
    const [cartaError, setCartaError] = useState("");

    // limpiar al cambiar de expediente
    useEffect(() => {
        setCartaOpen(false);
        setCartaInfo(null);
        setCartaError("");
    }, [idExpediente]);

    async function fetchCarta() {
        setLoadingCarta(true);
        setCartaError("");
        try {
            const resp = await expedienteCartaPoderJWT(idExpediente);
            if (resp?.ok && resp.data) {
                setCartaInfo(Array.isArray(resp.data) ? resp.data : [resp.data]);
            } else {
                setCartaError(resp?.mensaje || "No se encontró información");
                setCartaInfo([]);
            }
        } catch (e) {
            console.error("Error al cargar carta poder:", e);
            setCartaError("Error al cargar abogados");
            setCartaInfo(null);
        } finally {
            setLoadingCarta(false);
        }
    }

    const handleToggleCarta = async () => {
        const next = !cartaOpen;
        setCartaOpen(next);
        if (next && !cartaInfo && !loadingCarta) {
            await fetchCarta();
        }
    };

    const [snack, setSnack] = useState({ open: false, msg: "", sev: "info" });
    const openSnack = (msg, sev = "info") => setSnack({ open: true, msg, sev });
    const closeSnack = () => setSnack(s => ({ ...s, open: false }));

    const LINE = { display: "flex", alignItems: "center", gap: 8, lineHeight: 1.6, marginBottom: 4 };

    const fmtMoney = (v) =>
        (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString());


  return (
      <Box>
          {/* CARD RADICACION */}
          <Card style={{ marginBottom: 24 }}>
            <CardHeader
              style={{ background: COLOR_HEADER, color: COLOR_HEADER_TEXT }}
              title="Datos de Radicación"
              titleTypographyProps={{ style: { color: "#fff", fontWeight: 600, fontSize: 18, letterSpacing: 1 } }}
              action={canUpdate && (
                <Button
                  variant="outlined"
                  style={{ background: "#fff", color: "#5d7b9d", fontWeight: 600, marginLeft: 12, textTransform: "none" }}
                  size="small"
                  onClick={() => setModalOpen(true)}
                >
                  EDITAR
                </Button>
              )}
            />
            <CardContent>
              {loading ? (
                <CircularProgress />
              ) : data ? (
                <Grid container spacing={2}>
                  {/* Columna izquierda */}
                  <Grid size={{ xs: 12, md: 6 }}>
                                      <Typography style={LINE}><b>Abogado Apoderado:</b>&nbsp;{safeCap(data.apoderado)}</Typography>
                                      <Typography style={LINE}><b>Domicilio constituido:</b>&nbsp;{safeCap(data.domicilio)}</Typography>
                                      <Typography style={LINE}>
                                          <b>Carta poder:</b>&nbsp;{data.carta_poder ? data.carta_poder : "—"}
                                          <Button size="small" style={{ marginLeft: 8 }} onClick={handleToggleCarta}>
                                              {cartaOpen ? "Ocultar" : "Ver abogados"}
                                          </Button>
                                      </Typography>

                                      {cartaOpen && (
                                          <Box mt={1} mb={1}>
                                              {loadingCarta && <CircularProgress size={20} />}
                                              {!loadingCarta && cartaError && (
                                                  <Typography variant="body2" color="error">{cartaError}</Typography>
                                              )}
                                              {!loadingCarta && Array.isArray(cartaInfo) && cartaInfo.length > 0 && (
                                                  <TableContainer>
                                                      <Table size="small">
                                                          <TableHead>
                                                              <TableRow style={{ background: "#f0f2f7" }}>
                                                                  <TableCell>Carta Poder</TableCell>
                                                                  <TableCell>Abogado</TableCell>
                                                                  <TableCell>Matrícula</TableCell>
                                                                  <TableCell>Activo</TableCell>
                                                              </TableRow>
                                                          </TableHead>
                                                          <TableBody>
                                                              {cartaInfo.map((row, i) => (
                                                                  <TableRow key={row.id || i} style={i % 2 ? { background: COLOR_ROW_ALT } : {}}>
                                                                      <TableCell>{row.carta_poder || "—"}</TableCell>
                                                                      <TableCell>{capitalizar(row.abogado || "—")}</TableCell>
                                                                      <TableCell>{row.matricula || "—"}</TableCell>
                                                                      <TableCell>{row.activo}</TableCell>
                                                                  </TableRow>
                                                              ))}
                                                          </TableBody>
                                                      </Table>
                                                  </TableContainer>
                                              )}
                                              {!loadingCarta && Array.isArray(cartaInfo) && cartaInfo.length === 0 && (
                                                  <Typography variant="body2" color="text.secondary" style={{ fontStyle: 'italic' }}>
                                                      No hay abogados apoderados asignados.
                                                  </Typography>
                                              )}
                                          </Box>
                                      )}
                                      <Typography style={LINE}><b>Juzgado:</b>&nbsp;{capitalizar(textoJuzgado || "—")}</Typography>
                                      <Typography style={LINE}><b>Fecha de radicación:</b>&nbsp;{data.fecha_radicacion || "—"}</Typography>
                  </Grid>

                  {/* Columna derecha */}
                  <Grid size={{ xs: 12, md: 6 }}>
                      {/* Módulos que NO son Ejecuciones: tres montos manuales, como el sistema
                          viejo desde sept-2026. "Monto demanda" ahí es la columna
                          Monto_Actualizado_Demanda — en esos módulos siempre fue el monto de la
                          demanda cargado a mano. Ver el comentario en getRadicacion (backend).
                          es_ejecucion ausente (backend viejo) → layout de siempre. */}
                      {data.es_ejecucion === false ? (
                        <>
                          <MontoEditable label="Monto demanda" valor={data.monto_actualizado}
                            moneda={data.moneda} canUpdate={canUpdate} style={LINE}
                            onGuardar={(v, m) => guardarMontoNoEjecucion('monto_actualizado', v, m, 'Monto demanda')} />
                          <MontoEditable label="Monto actualizado demanda" valor={data.new_monto_actualizado}
                            moneda={data.moneda} canUpdate={canUpdate} style={LINE}
                            onGuardar={(v, m) => guardarMontoNoEjecucion('new_monto_actualizado', v, m, 'Monto actualizado demanda')} />
                          <MontoEditable label="Monto ahorrado demanda" valor={data.new_monto_ahorrado}
                            moneda={data.moneda} canUpdate={canUpdate} style={LINE}
                            onGuardar={(v, m) => guardarMontoNoEjecucion('new_monto_ahorrado', v, m, 'Monto ahorrado demanda')} />
                        </>
                      ) : (<>
                      {/* MONTO DEMANDA (editable). Si está seteado manualmente, gana sobre
                          el cálculo automático desde certificados. */}
                      <Box style={LINE}>
                        <b>Monto demanda:</b>
                        {editingMontoDemanda ? (
                          <>
                            <TextField
                              select
                              value={nuevaMoneda}
                              onChange={e => setNuevaMoneda(e.target.value)}
                              size="small"
                              style={{ width: 80, marginLeft: 8 }}
                              SelectProps={{ native: true }}
                            >
                              <option value="ARS">ARS</option>
                              <option value="USD">USD</option>
                            </TextField>
                            <TextField
                              value={nuevoMontoDemanda}
                              onChange={e => setNuevoMontoDemanda(formatMilesAR(e.target.value))}
                              type="text"
                              size="small"
                              style={{ width: 160, marginLeft: 8 }}
                              placeholder="0,00"
                            />
                            <Button size="small" onClick={handleSaveMontoDemanda} color="primary" style={{ marginLeft: 8 }} variant="contained">
                              Guardar
                            </Button>
                            <Button size="small" onClick={() => setEditingMontoDemanda(false)} style={{ marginLeft: 4 }}>
                              Cancelar
                            </Button>
                          </>
                        ) : (
                          <>
                            &nbsp;{moneySymbol(data.moneda)}{n0(data.monto_demanda).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            {canUpdate && (
                              <Button size="small" style={{ marginLeft: 8 }} onClick={() => {
                                setEditingMontoDemanda(true);
                                setNuevoMontoDemanda(formatMilesAR(String(data.monto_demanda ?? '').replace('.', ',')));
                                setNuevaMoneda(data.moneda || 'ARS');
                              }}>
                                Editar
                              </Button>
                            )}
                          </>
                        )}
                      </Box>

                      <Box style={LINE}>
                        <b>Monto actualizado demanda:</b>
                            {editingMontoAct ? (
                        <>
                        <TextField
                            select
                            value={nuevaMoneda}
                            onChange={e => setNuevaMoneda(e.target.value)}
                            size="small"
                            style={{ width: 80, marginLeft: 8 }}
                            SelectProps={{ native: true }}
                        >
                            <option value="ARS">ARS</option>
                            <option value="USD">USD</option>
                        </TextField>
                        <TextField
                            value={nuevoMonto}
                            onChange={e => setNuevoMonto(formatMilesAR(e.target.value))}
                            type="text"
                            size="small"
                            style={{ width: 160, marginLeft: 8 }}
                            placeholder="0,00"
                        />
                        <Button size="small" onClick={handleSaveMontoActualizado} color="primary" style={{ marginLeft: 8 }} variant="contained">
                            Guardar
                        </Button>
                        <Button size="small" onClick={() => setEditingMontoAct(false)} style={{ marginLeft: 4 }}>
                            Cancelar
                        </Button>
                        </>
                      ) : (
                        <>
                          &nbsp;{moneySymbol(data.moneda)}{n0(data.monto_actualizado).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                  {canUpdate && (
                                                      <Button size="small" style={{ marginLeft: 8 }} onClick={() => {
                                                          setEditingMontoAct(true);
                                                          setNuevoMonto(formatMilesAR(String(data.monto_actualizado ?? '').replace('.', ',')));
                                                          setNuevaMoneda(data.moneda || 'ARS');
                                                      }}>
                                                          Editar
                                                      </Button>
                                                  )}
                                              </>
                                          )}
                       </Box>
                      </>)}
                      <Typography style={LINE}><b>Presupuesto p/ costas:</b> {fmtMoney(data.presupuesto_costas)}</Typography>
                      <Typography style={LINE}><b>Ubicación del expediente:</b>&nbsp;{safeCap(data.ubicacion)}</Typography>
                  </Grid>
                </Grid>
              ) : (
                <Typography color="text.secondary">No se pudieron cargar los datos.</Typography>
              )}
            </CardContent>
          </Card>
          {/* TABLA DE AUTOS */}
          <Card style={{ marginBottom: 24 }}>
            <CardHeader
              style={{ background: COLOR_HEADER, color: COLOR_HEADER_TEXT }}
              title="Instancia"
              titleTypographyProps={{ style: { color: "#fff", fontWeight: 600, fontSize: 18, letterSpacing: 1 } }}
            />
            <CardContent style={{ padding: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ background: "#f0f2f7" }}>
                      <TableCell>Instancia</TableCell>
                      <TableCell>Autos</TableCell>
                      <TableCell>Fecha Sentencia</TableCell>
                      <TableCell>Resultado</TableCell>
                      {canUpdate && <TableCell></TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {autosDraft.map((row, idx) => (
                      <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                        <TableCell>{row.nombre}</TableCell>
                        <TableCell>
                          {editRow === idx ? (
                            <TextField
                              value={row.autos || ""}
                              onChange={e => {
                                const copy = [...autosDraft];
                                copy[idx].autos = e.target.value;
                                setAutosDraft(copy);
                              }}
                              size="small"
                            />
                          ) : (row.autos || "—")}
                        </TableCell>
                        <TableCell>
                          {editRow === idx ? (
                            <TextField
                              type="date"
                              value={toISO(row.fecha)}
                              onChange={e => {
                                const copy = [...autosDraft];
                                copy[idx].fecha = e.target.value;
                                setAutosDraft(copy);
                              }}
                              size="small"
                              InputLabelProps={{ shrink: true }}
                            />
                          ) : (toISO(row.fecha) || "—")}
                        </TableCell>
                        <TableCell>
                          {editRow === idx ? (
                            <TextField
                              select
                              value={row.resultado || ""}
                              onChange={e => {
                                const copy = [...autosDraft];
                                copy[idx].resultado = e.target.value;
                                setAutosDraft(copy);
                              }}
                              size="small"
                              style={{ minWidth: 140 }}
                            >
                              {RESULTADOS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                              ))}
                            </TextField>
                                ) : verResultado(row.resultado) }
                        </TableCell>
                        {canUpdate && (
                          <TableCell>
                            {editRow === idx ? (
                              <>
                                <Button size="small" color="primary" variant="contained" onClick={() => handleSaveAuto(idx)}>
                                  Guardar
                                </Button>
                                <Button size="small" onClick={() => setEditRow(null)} style={{ marginLeft: 8 }}>
                                  Cancelar
                                </Button>
                              </>
                            ) : (
                              <Button size="small" onClick={() => setEditRow(idx)} variant="outlined">
                                Editar
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
          {/* HISTORIAL */}
          <Card style={{ marginBottom: 24 }}>
            <CardHeader
              style={{ background: COLOR_HEADER, color: COLOR_HEADER_TEXT }}
              title="Historial de Radicación"
              titleTypographyProps={{ style: { color: "#fff", fontWeight: 600, fontSize: 18, letterSpacing: 1 } }}
            />
            <CardContent style={{ padding: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ background: "#f0f2f7" }}>
                      <TableCell>Juzgado</TableCell>
                      <TableCell>Fecha Radicación</TableCell>
                      <TableCell>Activo</TableCell>
                      <TableCell>Eliminar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingHistorial ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center"><CircularProgress size={24} /></TableCell>
                      </TableRow>
                    ) : historial.length > 0 ? (
                      historial.map((row, i) => (
                        <TableRow key={row.id || i} style={i % 2 ? { background: COLOR_ROW_ALT } : {}}>
                          <TableCell>{nombreJuzgadoPorId(row.juzgado)}</TableCell>
                          <TableCell>{row.fecha}</TableCell>
                          <TableCell>{row.activo ? "SI" : "NO"}</TableCell>
                              <TableCell>
                                  {canDelete ? (
                                      <Tooltip title="Eliminar registro de radicación">
                                          <IconButton
                                              size="small"
                                              onClick={() => pedirConfirmDelete(row)}
                                              aria-label="Eliminar"
                                          >
                                              <DeleteForeverIcon color="error" />
                                          </IconButton>
                                      </Tooltip>
                                  ) : (
                                      "—"
                                  )}
                              </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" style={{ color: "#999" }}>
                          No hay historial.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
          {/* CONFIRMAR ELIMINACIÓN HISTORIAL */}
          <Dialog open={deleteDlg.open} onClose={cerrarDeleteDlg} maxWidth="xs" fullWidth>
              <DialogTitle style={{ background: COLOR_HEADER, color: COLOR_HEADER_TEXT }}>
                  Confirmar eliminación
              </DialogTitle>
              <DialogContent>
                  <Typography>
                      ¿Eliminar el registro de radicación del{" "}
                      <b>{deleteDlg.target?.fecha || "-"}</b> en el juzgado{" "}
                      <b>{deleteDlg.target?.juzgado_descrip || deleteDlg.target?.juzgado || "-"}</b>?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" style={{ marginTop: 8 }}>
                      Esta acción solo quita esta fila del historial. El expediente no se verá afectado.
                  </Typography>
              </DialogContent>
              <DialogActions>
                  <Button onClick={cerrarDeleteDlg}>Cancelar</Button>
                  <Button color="secondary" variant="contained" onClick={handleDeleteHistorial}>
                      Eliminar
                  </Button>
              </DialogActions>
          </Dialog>
          {/* MODAL EDICIÓN GENERAL */}
          <RadicacionEditModal
            open={modalOpen}
            data={data}
            onClose={() => setModalOpen(false)}
                  onSave={async (nuevoData) => {
                      try {
                          const payload = {
                              ...nuevoData,
                              monto_demanda: normalizarMontoOutbound(nuevoData.monto_demanda),
                              monto_actualizado: normalizarMontoOutbound(nuevoData.monto_actualizado),
                              presupuesto_costas: normalizarMontoOutbound(nuevoData.presupuesto_costas),
                              new_monto_actualizado: normalizarMontoOutbound(nuevoData.new_monto_actualizado),
                              new_monto_ahorrado: normalizarMontoOutbound(nuevoData.new_monto_ahorrado),
                          };
                          const resp = await guardarRadicacionJWT(idExpediente, payload);
                          if (resp?.ok) {
                              openSnack("Radicación guardada", "success");
                              fetchAll();
                          } else {
                              openSnack(resp?.mensaje || "Error al guardar", "error");
                          }
                      } catch (err) {
                          console.error("Error guardando radicación:", err);
                          openSnack("Error al guardar radicación", "error");
                      }
                      setModalOpen(false);
                  }}


            juzgados={juzgados}
            domicilios={domicilios}
            domicilioSelId={selectedDomicilio}
              />
          <Snackbar open={snack.open} autoHideDuration={4000} onClose={closeSnack}>
              <MuiAlert elevation={6} variant="filled" onClose={closeSnack} severity={snack.sev}>
                  {snack.msg}
              </MuiAlert>
          </Snackbar>
      </Box>
  );
}

function RadicacionEditModal({ open, data, onClose, onSave, juzgados, domicilios, domicilioSelId }) {
    const [form, setForm] = useState({
        apoderado: "",
        carta_poder: "",
        domicilio: "",
        id_juzgado: "",
        presupuesto_costas: "",
        monto_demanda: "",
        monto_actualizado: "",
        new_monto_actualizado: "",
        new_monto_ahorrado: "",
        ubicacion: "",
        fecha_radicacion: ""   // <- NUEVO
    });

  useEffect(() => {
      if (open && data) {
          // 1) buscar ID en distintas claves
              let domId =
                  data.id_domicilio ??
                  data.id_domicilio_constituido ??
                  data.Id_Domicilio_Constituido ??
                  data.domicilio_id ??
                  null;
              // 2) si no vino en data, usar el seleccionado del endpoint de domicilios
              if (!domId && domicilioSelId) domId = domicilioSelId;
          
              // 3) último recurso: matchear por label de texto
              if (!domId && data.domicilio && Array.isArray(domicilios) && domicilios.length) {
                  const byLabel = domicilios.find(
                      d => String(d.label || "").trim().toLowerCase() === String(data.domicilio || "").trim().toLowerCase()
                          );
                if (byLabel) domId = byLabel.id;
                }

          setForm({
              apoderado: data.apoderado || "",
              carta_poder: data.carta_poder || "",
              domicilio: domId == null ? "" : String(domId),
              id_juzgado: data.id_juzgado || "",
              presupuesto_costas: data.presupuesto_costas ?? "",
              monto_demanda: data.monto_demanda ?? "",
              monto_actualizado: data.monto_actualizado ?? "",
              new_monto_actualizado: data.new_monto_actualizado ?? "",
              new_monto_ahorrado: data.new_monto_ahorrado ?? "",
              ubicacion: data.ubicacion || "",
              fecha_radicacion: toISO(data.fecha_radicacion) || ""
          });
      }
  }, [data, open, domicilioSelId, domicilios]);

    const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const getIdAny = (o) => (o && (o.id ?? o.Id));

  return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
          <DialogTitle style={{ background: "#466284", color: "#fff" }}>
            Editar Radicación
          </DialogTitle>
          <DialogContent>
                <TextField
                      label="Apoderado"
                      name="apoderado"
                      value={form.apoderado}
                      margin="dense"
                      fullWidth
                      disabled
                      helperText="Este dato se gestiona desde la pestaña Partes."
                  />

                  <TextField
                      label="Domicilio Constituido"
                      value={data?.domicilio || ""}
                      margin="dense"
                      fullWidth
                      disabled
                      helperText="Este dato se gestiona desde la configuración del módulo."
                  />

            <TextField label="Carta poder" name="carta_poder" value={form.carta_poder} margin="dense" fullWidth disabled
                helperText="Este dato se gestiona desde la pestaña Partes." />

            <Autocomplete
              options={[...juzgados].sort((a, b) => {
                  const fA = (a.nombre || '').split(' | ')[1] || '';
                  const fB = (b.nombre || '').split(' | ')[1] || '';
                  return fA.localeCompare(fB) || (a.nombre || '').localeCompare(b.nombre || '');
              })}
              groupBy={(option) => toTitleCase((option.nombre || '').split(' | ')[1] || 'Sin Fuero')}
              getOptionLabel={opt => {
                  const parts = (opt.nombre || '').split(' | ');
                  const nombre = toTitleCase(parts[0] || '');
                  const fuero = toTitleCase(parts[1] || '');
                  const jurisd = toTitleCase(parts[2] || '');
                  return [nombre, fuero, jurisd].filter(Boolean).join(' - ');
              }}
              isOptionEqualToValue={(option, value) => String(getIdAny(option)) === String(getIdAny(value))}
              value={juzgados.find(j => String(getIdAny(j)) === String(form.id_juzgado)) || null}
              onChange={(e, val) => setForm(prev => ({ ...prev, id_juzgado: val ? String(getIdAny(val)) : "" }))}
              renderOption={(props, option, { inputValue, index }) => {
                  const { key, ...rest } = props;
                  const parts = (option.nombre || '').split(' | ');
                  const nombre = toTitleCase(parts[0] || '');
                  const jurisdiccion = toTitleCase(parts[2] || '');
                  return (
                      <li key={option.id} {...rest} style={{ ...rest.style, ...zebraStyle(index), alignItems: 'flex-start', padding: '6px 12px' }}>
                          <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>{highlightMatch(nombre, inputValue)}</Typography>
                              {jurisdiccion && <Typography variant="caption" color="text.secondary">{jurisdiccion}</Typography>}
                          </Box>
                      </li>
                  );
              }}
              renderGroup={(params) => (
                  <li key={params.key}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', px: 1.5, py: 0.5, bgcolor: '#e3f2fd', display: 'block', position: 'sticky', top: -8, zIndex: 1 }}>
                          {params.group}
                      </Typography>
                      <ul style={{ padding: 0 }}>{params.children}</ul>
                  </li>
              )}
              slotProps={{ popper: { sx: { width: 'fit-content !important', minWidth: 450 } } }}
              renderInput={params => (<TextField {...params} label="Juzgado" margin="dense" fullWidth />)}
            />

                  <TextField
                      label="Fecha de radicación"
                      type="date"
                      name="fecha_radicacion"
                      value={form.fecha_radicacion || ""}
                      onChange={handleChange}
                      margin="dense"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                  />

            <TextField label="Presupuesto p/costas" name="presupuesto_costas" value={form.presupuesto_costas ?? ""} onChange={handleChange} margin="dense" fullWidth />
            {data?.es_ejecucion === false ? (
              <>
                {/* Mismos rótulos que el bloque de montos: ver comentario ahí */}
                <TextField label="Monto Demanda" name="monto_actualizado" value={form.monto_actualizado ?? ""} onChange={handleChange} margin="dense" fullWidth />
                <TextField label="Monto actualizado demanda" name="new_monto_actualizado" value={form.new_monto_actualizado ?? ""} onChange={handleChange} margin="dense" fullWidth />
                <TextField label="Monto ahorrado demanda" name="new_monto_ahorrado" value={form.new_monto_ahorrado ?? ""} onChange={handleChange} margin="dense" fullWidth />
              </>
            ) : (
              <>
                <TextField label="Monto Demanda" name="monto_demanda" value={form.monto_demanda ?? ""} onChange={handleChange} margin="dense" fullWidth />
                <TextField label="Monto actualizado demanda" name="monto_actualizado" value={form.monto_actualizado ?? ""} onChange={handleChange} margin="dense" fullWidth />
              </>
            )}
            <TextField label="Ubicación" name="ubicacion" value={form.ubicacion || ""} onChange={handleChange} margin="dense" fullWidth />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => onSave(form)}
            >
              Guardar
            </Button>
          </DialogActions>
      </Dialog>
  );
}
